import React from 'react';
import type { UsTaxImplicationsResult } from '@capintel/instruments-lib';

import CurrencyCell from '@/views/components/cells/CurrencyCell';
import FormattedMessageV2 from '@/views/components/i18n/FormattedMessageV2';
import type { Row } from '../TaxAnalysisImplicationsSections';

export function getShortTermRows(): Row<UsTaxImplicationsResult>[] {
  return [
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.ShortTerm.Gains" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.ShortTerm.Gains',
      meta: { isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.shortTerm?.gains;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.ShortTerm.Loss" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.ShortTerm.Loss',
      meta: { isLossRow: true, isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.shortTerm?.losses;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.ShortTerm.Total" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.ShortTerm.Total',
      meta: { isLossRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.shortTerm?.totalGainLoss;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}

export function getLongTermRows(): Row<UsTaxImplicationsResult>[] {
  return [
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.LongTerm.Gains" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.LongTerm.Gains',
      meta: { isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.longTerm?.gains;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.LongTerm.Loss" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.LongTerm.Loss',
      meta: { isLossRow: true, isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.longTerm?.losses;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.LongTerm.Total" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.LongTerm.Total',
      meta: { isLossRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.longTerm?.totalGainLoss;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}

export function getTotalEstimatedTaxesRow(): Row<UsTaxImplicationsResult>[] {
  return [
    {
      label: <FormattedMessageV2 className="section-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.CapitalGainLossNet" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.CapitalGainLossNet',
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.capitalGainLossNet;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="section-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.CapitalGainNetOfCarryforward" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.CapitalGainNetOfCarryforward',
      meta: { hasSectionDivider: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.capitalGainNetOfCarryforward;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="section-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.TotalEstimatedTaxes" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.TotalEstimatedTaxes',
      meta: { hasSectionDivider: true, isTall: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.totalEstimatedTaxes;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}

export function getCarryForwardRows(): Row<UsTaxImplicationsResult>[] {
  return [
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.CarryForward.ShortTerm" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.CarryForward.ShortTerm',
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.carryforwardBalance?.shortTerm;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.US.CarryForward.LongTerm" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.US.CarryForward.LongTerm',
      meta: { isLossRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.carryforwardBalance?.longTerm;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}
